import { Undo2, AlertTriangle } from 'lucide-react';
import { formatDateTimeLocal } from '../../utils/formatters';

/* TYPING-RETURN-TO-QC-001 — shown inside QC Review Mode when Typing sent the order back to QC.
   QC-REVIEW-COMPACT-002 (Oct 2026): collapsed to ONE compact alert row. Routing / fallback
   explanations moved to tooltips; the full reason + history live in the Timeline & Notes drawer.
   `actions` (View Details · + Add Document) are rendered on the right. */
const ROUTING_TIP = 'Routed to the QC Specialist who approved this order. Fulfillment data stays read-only: manage QC documents and re-approve to Typing, or use Return for Correction when the Search Processor must fix the search data.';

export const TypingReturnToQcBanner = ({ order, actions = null }) => {
  if (!order || order.status !== 'qc_review' || order.typing_status !== 'returned_to_qc') return null;
  const fallback = order.typing_return_fallback_reason;
  const reason = order.typing_return_reason || 'No reason recorded.';
  const by = order.typing_returned_by_name || 'Typing Processor';
  const when = order.typing_returned_at ? formatDateTimeLocal(order.typing_returned_at) : '';
  const fallbackTip = fallback
    ? `Original QC Specialist ${order.qc_approved_by_name || ''} could not take this return (${fallback}) — ${order.qc_assigned_to ? `now assigned to ${order.qc_assigned_to_name}.` : 'the order is in the unassigned QC queue.'}`
    : null;

  return (
    <div
      className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-rose-200/80 bg-rose-50/50 px-3 py-2 transition-[background-color,box-shadow] duration-200 hover:bg-rose-50/80 hover:shadow-sm"
      title={fallbackTip || ROUTING_TIP}
      data-testid="typing-return-to-qc-banner"
    >
      <Undo2 className="w-4 h-4 text-rose-600 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] leading-snug truncate">
          <span className="font-semibold text-rose-800" data-testid="typing-return-to-qc-title">Returned from Typing</span>
          <span className="text-rose-300 mx-1.5">·</span>
          <span className="text-slate-800" title={reason} data-testid="typing-return-to-qc-reason">{reason}</span>
        </p>
        <p className="text-[11.5px] text-slate-500 leading-snug truncate">
          Returned by <span className="font-medium text-slate-700" data-testid="typing-return-to-qc-by">{by}</span>{when ? ` · ${when}` : ''} · QC re-review required
          {fallbackTip && (
            <span className="inline-flex items-center gap-1 ml-2 text-amber-700" data-testid="typing-return-to-qc-fallback" title={fallbackTip}>
              <AlertTriangle className="w-3 h-3" aria-hidden="true" /> reassigned
            </span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        {order.qc_iteration ? (
          <span className="h-6 px-2 inline-flex items-center rounded-full bg-white border border-rose-200 text-[10.5px] font-semibold uppercase tracking-wide text-rose-700" data-testid="typing-return-to-qc-iteration">
            Iteration {order.qc_iteration}
          </span>
        ) : null}
        {actions}
      </div>
    </div>
  );
};

export default TypingReturnToQcBanner;
